import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import { useSnackbar } from 'notistack'

const VERSION_STORAGE_KEY = 'APP_VERSION'
const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

export const useVersionCheck = (): { currentVersion: string | null } => {
  const [currentVersion, setCurrentVersion] = useState<string | null>(
    () => localStorage.getItem(VERSION_STORAGE_KEY)
  )
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const snackbarKeyRef = useRef<string | number | null>(null)

  const applyUpdate = useCallback(async (snackbarId: string | number) => {
    closeSnackbar(snackbarId)
    try {
      // Store the new version BEFORE reloading to prevent infinite reload loops
      const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
      if (response.ok) {
        const data = await response.json()
        if (data?.version) {
          localStorage.setItem(VERSION_STORAGE_KEY, data.version)
        }
      }
    } catch {
      // Even if fetch fails, proceed with reload
    }
    window.location.reload()
  }, [closeSnackbar])

  const showUpdateNotification = useCallback(() => {
    // Don't show duplicate notifications
    if (snackbarKeyRef.current) return

    const key = enqueueSnackbar(
      'A new version is available. Please reload to get the latest updates.',
      {
        variant: 'info',
        persist: true,
        action: (snackbarId) =>
          createElement(Button, {
            color: 'inherit',
            size: 'small',
            variant: 'outlined',
            onClick: () => applyUpdate(snackbarId),
            sx: { ml: 1, whiteSpace: 'nowrap' }
          }, 'Reload Now')
      }
    )
    snackbarKeyRef.current = key
  }, [enqueueSnackbar, applyUpdate])

  const fetchVersion = useCallback(async () => {
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
      if (!response.ok) return

      const data = await response.json()
      const remoteVersion = data?.version
      if (!remoteVersion) return

      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY)

      if (!storedVersion) {
        // First visit — store the version, no prompt
        localStorage.setItem(VERSION_STORAGE_KEY, remoteVersion)
        setCurrentVersion(remoteVersion)
        return
      }

      if (storedVersion !== remoteVersion) {
        showUpdateNotification()
      }

      setCurrentVersion(storedVersion)
    } catch {
      // Silently ignore fetch errors
    }
  }, [showUpdateNotification])

  useEffect(() => {
    fetchVersion()
    const interval = setInterval(fetchVersion, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchVersion])

  return { currentVersion }
}
